import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
} from 'react';
import type { RaeditorStore } from '@reyka/openpolotno';
import { ensureStoreFonts } from '../ensureStoreFonts';
import { apiBase, withApiKey } from '../apiAuth';
import {
  fetchTemplateJson,
  ornamentsFromTemplate,
  patchStoreWithSvgs,
  svgToDataUrl,
  updateTemplateJson,
  uploadSvgAsset,
  type VectorizeTarget,
} from '../vectorize/ornamentsFromTemplate';

const MAX_WIDTH = 1280;
const MAX_BYTES = 4 * 1024 * 1024; // ~4MB target after compress
const MIN_CROP_PX = 8;

type Props = {
  open: boolean;
  onClose: () => void;
  onStatus: (message: string) => void;
  onSuccess?: (result: { uniqueId: string; title: string }) => void;
  /** When provided, load the result into the live editor after convert (+ optional vectorize). */
  store?: RaeditorStore;
};

/** Crop rectangle in natural image pixel coordinates */
export type CropRect = { x: number; y: number; width: number; height: number };

type HandleId = 'nw' | 'ne' | 'sw' | 'se' | 'n' | 's' | 'e' | 'w';

function clampCrop(crop: CropRect, natW: number, natH: number): CropRect {
  let { x, y, width, height } = crop;
  width = Math.max(MIN_CROP_PX, Math.min(width, natW));
  height = Math.max(MIN_CROP_PX, Math.min(height, natH));
  x = Math.max(0, Math.min(x, natW - width));
  y = Math.max(0, Math.min(y, natH - height));
  // Re-clamp size if position pushed us past edge
  width = Math.min(width, natW - x);
  height = Math.min(height, natH - y);
  return {
    x: Math.round(x),
    y: Math.round(y),
    width: Math.max(MIN_CROP_PX, Math.round(width)),
    height: Math.max(MIN_CROP_PX, Math.round(height)),
  };
}

function fullCrop(natW: number, natH: number): CropRect {
  return { x: 0, y: 0, width: natW, height: natH };
}

/** Compress a canvas / ImageBitmap / similar to a data URL (resize + quality). */
async function compressImageSource(
  source: CanvasImageSource,
  srcW: number,
  srcH: number,
  preferType?: string,
): Promise<{ dataUrl: string; mimeType: string }> {
  let width = srcW;
  let height = srcH;
  if (width > MAX_WIDTH) {
    const scale = MAX_WIDTH / width;
    width = Math.round(width * scale);
    height = Math.round(height * scale);
  }
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas unavailable');
  ctx.drawImage(source, 0, 0, width, height);

  let quality = 0.92;
  let mimeType = 'image/jpeg';
  if (preferType === 'image/png' || preferType === 'image/webp') {
    mimeType = 'image/png';
  }
  let dataUrl = canvas.toDataURL(mimeType, quality);

  if (dataUrl.length > MAX_BYTES * 1.37) {
    mimeType = 'image/jpeg';
    quality = 0.85;
    while (quality >= 0.5) {
      dataUrl = canvas.toDataURL('image/jpeg', quality);
      if (dataUrl.length <= MAX_BYTES * 1.37) break;
      quality -= 0.1;
    }
  }
  if (dataUrl.length > 5_500_000) {
    throw new Error('Image is still too large after compression. Try a smaller file.');
  }
  return { dataUrl, mimeType };
}

/** Read file → resize/compress → data URL (full image). */
async function fileToDataUrl(file: File): Promise<{ dataUrl: string; mimeType: string }> {
  const bitmap = await createImageBitmap(file);
  try {
    return await compressImageSource(bitmap, bitmap.width, bitmap.height, file.type);
  } finally {
    bitmap.close();
  }
}

/** Crop region from file (image pixel coords) → compress → data URL. */
async function cropFileToDataUrl(
  file: File,
  crop: CropRect,
): Promise<{ dataUrl: string; mimeType: string }> {
  const sx = Math.max(0, Math.round(crop.x));
  const sy = Math.max(0, Math.round(crop.y));
  const sw = Math.max(1, Math.round(crop.width));
  const sh = Math.max(1, Math.round(crop.height));
  const bitmap = await createImageBitmap(file, sx, sy, sw, sh);
  try {
    return await compressImageSource(bitmap, bitmap.width, bitmap.height, file.type);
  } finally {
    bitmap.close();
  }
}

async function applyOrnamentVectorize(opts: {
  uniqueId: string;
  sourceUrl: string;
  targets: VectorizeTarget[];
  onProgress: (msg: string) => void;
}): Promise<{ vectorized: number; failed: number; storeJson: Record<string, unknown> | null }> {
  const { uniqueId, sourceUrl, targets, onProgress } = opts;
  if (!targets.length) {
    return { vectorized: 0, failed: 0, storeJson: null };
  }

  onProgress(`Vectorizing ornaments… (0/${targets.length})`);
  const results = await ornamentsFromTemplate(targets, sourceUrl, {
    onProgress: (done, total) => {
      onProgress(`Vectorizing ornaments… (${done}/${total})`);
    },
  });

  const replacements: Array<{ elementId: string; src: string }> = [];
  let vectorized = 0;
  let failed = 0;

  for (const r of results) {
    if (!r.ok) {
      failed++;
      continue;
    }
    let src = await uploadSvgAsset(uniqueId, r.elementId, r.svg);
    if (!src) {
      // Soft fallback: embed data URL so the SVG still appears
      src = svgToDataUrl(r.svg);
    }
    replacements.push({ elementId: r.elementId, src });
    vectorized++;
  }

  if (!replacements.length) {
    return { vectorized: 0, failed, storeJson: null };
  }

  onProgress('Updating template with SVG layers…');
  const storeJson = await fetchTemplateJson(uniqueId);
  if (!storeJson) {
    return { vectorized, failed: failed + replacements.length, storeJson: null };
  }

  const patched = patchStoreWithSvgs(storeJson, replacements);
  const saved = await updateTemplateJson(uniqueId, patched);
  if (!saved) {
    console.warn('Template JSON update failed; returning patched JSON for live store only');
  }
  return { vectorized, failed, storeJson: patched as Record<string, unknown> };
}

type DragMode =
  | { kind: 'create'; startX: number; startY: number }
  | { kind: 'move'; startX: number; startY: number; orig: CropRect }
  | { kind: 'resize'; handle: HandleId; startX: number; startY: number; orig: CropRect };

function ImageCropStage({
  src,
  crop,
  naturalSize,
  onNaturalSize,
  onCropChange,
  disabled,
}: {
  src: string;
  crop: CropRect | null;
  naturalSize: { w: number; h: number } | null;
  onNaturalSize: (size: { w: number; h: number }) => void;
  onCropChange: (crop: CropRect) => void;
  disabled?: boolean;
}) {
  const viewportRef = useRef<HTMLDivElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const [stage, setStage] = useState<{ w: number; h: number; scale: number } | null>(null);
  const dragRef = useRef<DragMode | null>(null);
  const [, setDragTick] = useState(0);

  const measure = useCallback(() => {
    const viewport = viewportRef.current;
    const img = imgRef.current;
    if (!viewport || !img || !img.naturalWidth || !img.naturalHeight) return;
    const natW = img.naturalWidth;
    const natH = img.naturalHeight;
    onNaturalSize({ w: natW, h: natH });
    const maxW = viewport.clientWidth;
    const maxH = viewport.clientHeight;
    if (maxW < 1 || maxH < 1) return;
    const scale = Math.min(maxW / natW, maxH / natH);
    setStage({ w: natW * scale, h: natH * scale, scale });
  }, [onNaturalSize]);

  useLayoutEffect(() => {
    measure();
  }, [src, measure]);

  useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport) return;
    const ro = new ResizeObserver(() => measure());
    ro.observe(viewport);
    return () => ro.disconnect();
  }, [measure]);

  const clientToImage = useCallback(
    (clientX: number, clientY: number): { x: number; y: number } | null => {
      const img = imgRef.current;
      if (!img || !stage) return null;
      const rect = img.getBoundingClientRect();
      const x = (clientX - rect.left) / stage.scale;
      const y = (clientY - rect.top) / stage.scale;
      return { x, y };
    },
    [stage],
  );

  const hitHandle = useCallback(
    (ix: number, iy: number, c: CropRect): HandleId | null => {
      if (!stage) return null;
      const tol = 12 / stage.scale;
      const points: Array<[HandleId, number, number]> = [
        ['nw', c.x, c.y],
        ['ne', c.x + c.width, c.y],
        ['sw', c.x, c.y + c.height],
        ['se', c.x + c.width, c.y + c.height],
        ['n', c.x + c.width / 2, c.y],
        ['s', c.x + c.width / 2, c.y + c.height],
        ['w', c.x, c.y + c.height / 2],
        ['e', c.x + c.width, c.y + c.height / 2],
      ];
      for (const [id, hx, hy] of points) {
        if (Math.abs(ix - hx) <= tol && Math.abs(iy - hy) <= tol) return id;
      }
      return null;
    },
    [stage],
  );

  const applyResize = (handle: HandleId, orig: CropRect, ix: number, iy: number, natW: number, natH: number): CropRect => {
    let { x, y, width, height } = orig;
    const right = orig.x + orig.width;
    const bottom = orig.y + orig.height;
    if (handle.includes('w')) {
      x = Math.min(ix, right - MIN_CROP_PX);
      width = right - x;
    }
    if (handle.includes('e')) {
      width = Math.max(MIN_CROP_PX, ix - orig.x);
    }
    if (handle.includes('n')) {
      y = Math.min(iy, bottom - MIN_CROP_PX);
      height = bottom - y;
    }
    if (handle.includes('s')) {
      height = Math.max(MIN_CROP_PX, iy - orig.y);
    }
    return clampCrop({ x, y, width, height }, natW, natH);
  };

  const onPointerDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (disabled || !naturalSize || !stage) return;
    const pt = clientToImage(e.clientX, e.clientY);
    if (!pt) return;
    e.preventDefault();
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);

    const c = crop ?? fullCrop(naturalSize.w, naturalSize.h);
    const handle = hitHandle(pt.x, pt.y, c);
    if (handle) {
      dragRef.current = { kind: 'resize', handle, startX: pt.x, startY: pt.y, orig: c };
    } else if (
      pt.x >= c.x &&
      pt.x <= c.x + c.width &&
      pt.y >= c.y &&
      pt.y <= c.y + c.height
    ) {
      dragRef.current = { kind: 'move', startX: pt.x, startY: pt.y, orig: c };
    } else {
      dragRef.current = { kind: 'create', startX: pt.x, startY: pt.y };
      onCropChange(
        clampCrop(
          { x: pt.x, y: pt.y, width: MIN_CROP_PX, height: MIN_CROP_PX },
          naturalSize.w,
          naturalSize.h,
        ),
      );
    }
    setDragTick((n) => n + 1);
  };

  const onPointerMove = (e: ReactPointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    if (!drag || !naturalSize) return;
    const pt = clientToImage(e.clientX, e.clientY);
    if (!pt) return;
    e.preventDefault();

    if (drag.kind === 'create') {
      const x1 = drag.startX;
      const y1 = drag.startY;
      const x2 = pt.x;
      const y2 = pt.y;
      onCropChange(
        clampCrop(
          {
            x: Math.min(x1, x2),
            y: Math.min(y1, y2),
            width: Math.abs(x2 - x1),
            height: Math.abs(y2 - y1),
          },
          naturalSize.w,
          naturalSize.h,
        ),
      );
    } else if (drag.kind === 'move') {
      const dx = pt.x - drag.startX;
      const dy = pt.y - drag.startY;
      onCropChange(
        clampCrop(
          {
            x: drag.orig.x + dx,
            y: drag.orig.y + dy,
            width: drag.orig.width,
            height: drag.orig.height,
          },
          naturalSize.w,
          naturalSize.h,
        ),
      );
    } else {
      onCropChange(applyResize(drag.handle, drag.orig, pt.x, pt.y, naturalSize.w, naturalSize.h));
    }
  };

  const endDrag = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (!dragRef.current) return;
    dragRef.current = null;
    try {
      (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId);
    } catch {
      /* already released */
    }
    setDragTick((n) => n + 1);
  };

  const activeCrop = crop && naturalSize ? crop : null;
  const stylePct = (v: number, total: number) => `${(v / total) * 100}%`;

  return (
    <div className="itt-crop-viewport" ref={viewportRef}>
      <div
        className={`itt-crop-stage${disabled ? ' itt-crop-stage--disabled' : ''}`}
        style={stage ? { width: stage.w, height: stage.h } : undefined}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
      >
        <img
          ref={imgRef}
          src={src}
          alt="Selected design preview"
          className="itt-crop-stage__img"
          draggable={false}
          onLoad={measure}
        />
        {activeCrop && naturalSize ? (
          <div
            className="itt-crop-rect"
            style={{
              left: stylePct(activeCrop.x, naturalSize.w),
              top: stylePct(activeCrop.y, naturalSize.h),
              width: stylePct(activeCrop.width, naturalSize.w),
              height: stylePct(activeCrop.height, naturalSize.h),
            }}
          >
            {(['nw', 'ne', 'sw', 'se', 'n', 's', 'e', 'w'] as HandleId[]).map((h) => (
              <span key={h} className={`itt-crop-handle itt-crop-handle--${h}`} />
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
}

export function ImageToTemplateModal({ open, onClose, onStatus, onSuccess, store }: Props) {
  const titleId = useId();
  const fileRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [naturalSize, setNaturalSize] = useState<{ w: number; h: number } | null>(null);
  const [crop, setCrop] = useState<CropRect | null>(null);
  const [title, setTitle] = useState('');
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState('');
  const [error, setError] = useState('');
  const [result, setResult] = useState<{
    uniqueId: string;
    title: string;
    mode?: string;
    vectorized?: number;
  } | null>(null);

  useEffect(() => {
    if (!open) return;
    setFile(null);
    setPreviewUrl(null);
    setNaturalSize(null);
    setCrop(null);
    setTitle('');
    setBusy(false);
    setProgress('');
    setError('');
    setResult(null);
    if (fileRef.current) fileRef.current.value = '';
  }, [open]);

  useEffect(() => {
    if (!file) {
      setPreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const handleNaturalSize = useCallback((size: { w: number; h: number }) => {
    setNaturalSize((prev) => {
      if (prev && prev.w === size.w && prev.h === size.h) return prev;
      return size;
    });
    setCrop((prev) => {
      if (prev) return clampCrop(prev, size.w, size.h);
      return fullCrop(size.w, size.h);
    });
  }, []);

  const onPick = useCallback((f: File | null) => {
    setError('');
    setResult(null);
    setNaturalSize(null);
    setCrop(null);
    if (!f) {
      setFile(null);
      return;
    }
    if (!f.type.startsWith('image/')) {
      setError('Please choose a PNG, JPG, or WebP image.');
      setFile(null);
      return;
    }
    setFile(f);
    setTitle((prev) => {
      if (prev) return prev;
      const base = f.name.replace(/\.[^.]+$/, '').slice(0, 80);
      return base || 'Imported image template';
    });
  }, []);

  const [dragOver, setDragOver] = useState(false);

  useEffect(() => {
    if (!open) return;
    const onPaste = (e: ClipboardEvent) => {
      const items = e.clipboardData?.items;
      if (!items) return;
      for (const item of items) {
        if (item.type.startsWith('image/')) {
          const f = item.getAsFile();
          if (f) {
            e.preventDefault();
            onPick(f);
          }
          break;
        }
      }
    };
    window.addEventListener('paste', onPaste);
    return () => window.removeEventListener('paste', onPaste);
  }, [open, onPick]);

  const resetCrop = () => {
    if (!naturalSize) return;
    setCrop(fullCrop(naturalSize.w, naturalSize.h));
  };

  const handleConvert = async () => {
    if (!file) {
      setError('Choose an image first.');
      return;
    }
    setBusy(true);
    setError('');
    setResult(null);
    setProgress('Preparing image…');
    onStatus('Image to template: preparing…');
    try {
      const activeCrop =
        crop && naturalSize
          ? clampCrop(crop, naturalSize.w, naturalSize.h)
          : null;
      const { dataUrl, mimeType } = activeCrop
        ? await cropFileToDataUrl(file, activeCrop)
        : await fileToDataUrl(file);
      setProgress('Identifying elements, matching fonts, building template…');
      onStatus('Image to template: converting…');
      const res = await fetch(withApiKey(`${apiBase()}/api/templates/from-image`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          imageDataUrl: dataUrl,
          title: title.trim() || 'Imported image template',
          mimeType,
        }),
      });
      const data = (await res.json().catch(() => ({}))) as {
        uniqueId?: string;
        title?: string;
        mode?: string;
        error?: string;
        message?: string;
        elementCount?: number;
        fontsUsed?: string[];
        source?: string;
        json?: string;
        vectorizeTargets?: VectorizeTarget[];
      };
      if (!res.ok) {
        throw new Error(data.error || data.message || `Request failed (${res.status})`);
      }

      const uniqueId = data.uniqueId || '';
      const savedTitle = data.title || title || 'Imported image template';
      const sourceUrl =
        data.source ||
        (uniqueId ? `/api/templates/${encodeURIComponent(`${uniqueId}-source`)}.png` : '');

      let vectorized = 0;
      let patchedJson: Record<string, unknown> | null = null;

      if (uniqueId && data.vectorizeTargets && data.vectorizeTargets.length > 0 && sourceUrl) {
        const orn = await applyOrnamentVectorize({
          uniqueId,
          sourceUrl,
          targets: data.vectorizeTargets,
          onProgress: (msg) => {
            setProgress(msg);
            onStatus(msg);
          },
        });
        vectorized = orn.vectorized;
        patchedJson = orn.storeJson;
      }

      // Prefer live store: load raster/template JSON then SVG replacements if we have them
      if (store && uniqueId) {
        try {
          setProgress('Loading into editor…');
          const json =
            patchedJson ||
            (await fetchTemplateJson(uniqueId)) ||
            null;
          if (json) {
            store.loadJSON(json);
            void ensureStoreFonts(store);
          }
        } catch (err) {
          console.warn('loadJSON after from-image failed', err);
        }
      }

      setResult({
        uniqueId,
        title: savedTitle,
        mode: data.mode,
        vectorized,
      });
      setProgress('');
      const modeNote =
        data.mode === 'ai-decomposition'
          ? ` · ${data.elementCount ?? '?'} layers · fonts: ${(data.fontsUsed || []).join(', ') || '—'}`
          : ' · fallback (set OPENAI_API_KEY for element decomposition)';
      const vecNote =
        vectorized > 0
          ? ` · ${vectorized} ornament${vectorized === 1 ? '' : 's'} vectorized`
          : data.vectorizeTargets?.length
            ? ' · ornament vectorize skipped/failed (raster kept)'
            : '';
      onStatus(`Saved to Templates: ${savedTitle}${modeNote}${vecNote}`);
      onSuccess?.({ uniqueId, title: savedTitle });
    } catch (err) {
      console.error(err);
      const msg = err instanceof Error ? err.message : String(err);
      setError(msg);
      setProgress('');
      onStatus(`Image to template failed: ${msg}`);
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  return (
    <div className="modal-backdrop" role="presentation" onClick={onClose}>
      <div
        className="modal modal--itt"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal__header">
          <h2 id={titleId} className="modal__title">
            Image to template
          </h2>
          <button type="button" className="modal__close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>

        <p className="modal__lead">
          Upload a design image, crop the region to convert, then we identify text, photos, and
          shapes, match Google Fonts, auto-vectorize ornaments (browser WASM VTracer), and save an
          editable OpenPolotno template to R2.
        </p>

        <label className="modal__label" htmlFor="itt-file">
          Design image (PNG / JPG / WebP)
        </label>
        <div
          className={`modal__dropzone${dragOver ? ' modal__dropzone--active' : ''}${busy ? ' modal__dropzone--disabled' : ''}${previewUrl ? ' modal__dropzone--compact' : ''}`}
          onDragEnter={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (!busy) setDragOver(true);
          }}
          onDragOver={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (!busy) setDragOver(true);
          }}
          onDragLeave={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setDragOver(false);
          }}
          onDrop={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setDragOver(false);
            if (busy) return;
            const f = e.dataTransfer.files?.[0] ?? null;
            onPick(f);
          }}
        >
          {previewUrl ? (
            <p className="modal__dropzone-hint" style={{ marginBottom: 8 }}>
              Drop, paste, or browse to replace the image (resets crop).
            </p>
          ) : (
            <>
              <p className="modal__dropzone-title">Drop an image here</p>
              <p className="modal__dropzone-hint">
                Or paste (Ctrl/Cmd+V). Native Open dialogs often fail on the remote desktop —
                drag/paste works better.
              </p>
            </>
          )}
          <input
            id="itt-file"
            ref={fileRef}
            type="file"
            accept="image/*"
            disabled={busy}
            onChange={(e) => onPick(e.target.files?.[0] ?? null)}
          />
        </div>

        {previewUrl ? (
          <div className="itt-crop">
            <div className="itt-crop__toolbar">
              <span className="itt-crop__hint">
                Drag to select · drag box to move · handles to resize
              </span>
              <button
                type="button"
                className="btn btn--ghost-dark itt-crop__reset"
                onClick={resetCrop}
                disabled={busy || !naturalSize}
              >
                Reset crop
              </button>
            </div>
            <ImageCropStage
              src={previewUrl}
              crop={crop}
              naturalSize={naturalSize}
              onNaturalSize={handleNaturalSize}
              onCropChange={setCrop}
              disabled={busy}
            />
            {naturalSize && crop ? (
              <p className="itt-crop__meta">
                Crop {crop.width}×{crop.height}px of {naturalSize.w}×{naturalSize.h}px
              </p>
            ) : null}
          </div>
        ) : null}

        <label className="modal__label" htmlFor="itt-title">
          Title (optional)
        </label>
        <input
          id="itt-title"
          className="modal__input"
          type="text"
          value={title}
          disabled={busy}
          placeholder="Imported image template"
          onChange={(e) => setTitle(e.target.value)}
        />

        {progress ? (
          <div className="modal__status" role="status">
            {progress}
          </div>
        ) : null}
        {error ? (
          <div className="modal__error" role="alert">
            {error}
          </div>
        ) : null}
        {result ? (
          <div className="modal__success" role="status">
            <strong>{result.title}</strong>
            <div>
              Saved to Templates
              {store ? ' and loaded in the editor' : ' — open Templates panel'}
              {result.mode === 'fallback'
                ? ' (fallback layout; set Worker secret OPENAI_API_KEY for AI decomposition)'
                : result.mode === 'ai-decomposition'
                  ? ' (AI-decomposed layers)'
                  : ''}
              {result.vectorized
                ? ` · ${result.vectorized} ornament${result.vectorized === 1 ? '' : 's'} as SVG`
                : ''}
            </div>
          </div>
        ) : null}

        <div className="modal__actions">
          <button type="button" className="btn btn--ghost-dark" onClick={onClose} disabled={busy}>
            {result ? 'Close' : 'Cancel'}
          </button>
          <button
            type="button"
            className="btn btn--accent"
            onClick={handleConvert}
            disabled={busy || !file || Boolean(result)}
          >
            {busy ? 'Converting…' : 'Convert to template'}
          </button>
        </div>
      </div>
    </div>
  );
}
